import React, { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import moment from 'moment';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap';
import * as Icons from 'react-feather';
import 'react-datepicker/dist/react-datepicker.css';
import PageHeader from '../../shared/page-header/PageHeader';
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts';
import { getStorageValues } from '../../shared/CommonFunctions';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { getModulePermissions } from '../../utils/permissionHelper';
import ClientCommunicationHistoryGrid from './ClientCommunicationHistoryGrid';
import ClientDropdown from '../../shared/common-dropdown/ClientDropdown.jsx';
import { logAuditEvent } from '../../utils/auditLogger';

const ClientCommunication = ({ Module }) => {
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClientOption, setSelectedClientOption] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [clientInvestments, setClientInvestments] = useState([]);
  const [companyTypeOptions, setCompanyTypeOptions] = useState([]);
  const [selectedCompanyTypes, setSelectedCompanyTypes] = useState([]);
  const [remark, setRemark] = useState('');
  const [nextFollowupDate, setNextFollowupDate] = useState(null);
  const [history, setHistory] = useState([]);
  const [alert, setAlert] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [selectedDeleteEntry, setSelectedDeleteEntry] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [userModulePermission, setUserModulePermission] = useState({});
  const moduleName = Module || 'Client Communication';

  const fetchClientCompanyTypes = useCallback(async () => {
    try {
      const response = await axios.get(getApiBaseUrl() + '/clientCompanyTypelist');
      if (response.status === 200) {
        const list = response.data.data[0] || [];
        setCompanyTypeOptions(
          list.map((type) => ({
            value: type.ClientCompanyTypeId,
            label: type.ClientCompanyTypeName
          }))
        );
      }
    } catch (error) {
      console.log(error);
      CreateAlert('Unable to load client company type list!', 'Error', setAlert);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    setIsLoadingClients(true);
    try {
      const params = {
        UserId: getStorageValues('userId'),
        IsForDropodown: 0
      };
      const response = await axios.get(getApiBaseUrl() + '/clientlist', { params });
      if (response.status === 200) {
        const list = response.data.data[0] || [];
        setClientOptions(
          list.map((client) => ({
            value: client.ClientId,
            label: client.ClientDisplayName || client.ClientName,
            raw: client
          }))
        );
        if (selectedClientOption) {
          const updatedClient = list.find((x) => x.ClientId === selectedClientOption.value);
          if (updatedClient) {
            setClientDetails(updatedClient);
          } else {
            setSelectedClientOption(null);
            setSelectedCompanyTypes([]);
            setRemark('');
            setNextFollowupDate(null);
            setClientDetails(null);
            setClientInvestments([]);
            setHistory([]);
          }
        }
      }
    } catch (error) {
      console.log(error);
      CreateAlert('Unable to load clients right now!', 'Error', setAlert);
    } finally {
      setIsLoadingClients(false);
    }
  }, [selectedClientOption]);

  useEffect(() => {
    const modulePermissions = getModulePermissions(Module);
    setUserModulePermission(modulePermissions ? modulePermissions : {});
    fetchClients();
    fetchClientCompanyTypes();
  }, [Module, fetchClients, fetchClientCompanyTypes]);

  useEffect(() => {
    logAuditEvent(moduleName, 'Accessed Module', 'Accessed Client Communication');
  }, [moduleName]);

  const fetchClientInvestments = async (clientId) => {
    if (!clientId) {
      setClientInvestments([]);
      return;
    }
    try {
      const params = {
        UserId: getStorageValues('userId'),
        ClientId: clientId
      };
      const response = await axios.get(getApiBaseUrl() + '/clientinvestments', { params });
      if (response.status === 200) {
        setClientInvestments(response.data.data[0] || []);
      }
    } catch (error) {
      console.log(error);
      CreateAlert('Unable to load investment details!', 'Error', setAlert);
    }
  };

  const fetchCommunicationHistory = async (clientId) => {
    if (!clientId) {
      setHistory([]);
      return;
    }
    setIsLoadingHistory(true);
    try {
      const params = {
        ClientId: clientId,
        Limit: 10
      };
      const response = await axios.get(getApiBaseUrl() + '/clientCommunicationHistory', { params });
      if (response.status === 200) {
        setHistory(response.data.data || []);
      }
    } catch (error) {
      console.log(error);
      CreateAlert('Unable to fetch communication history!', 'Error', setAlert);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleClientSelection = (option) => {
    setSelectedClientOption(option);
    setSelectedCompanyTypes([]);
    setRemark('');
    setNextFollowupDate(null);
    if (option) {
      logAuditEvent(moduleName, 'Client Selected', `ClientId=${option.value}`);
    }
    if (option) {
      setClientDetails(option.raw);
      fetchClientInvestments(option.value);
      fetchCommunicationHistory(option.value);
    } else {
      setClientDetails(null);
      setClientInvestments([]);
      setHistory([]);
    }
  };

  const handleClientDropdownChange = (option) => {
    if (!option) {
      handleClientSelection(null);
      return;
    }
    const matchedClient = clientOptions.find((client) => client.value === option.value);
    if (matchedClient) {
      handleClientSelection(matchedClient);
    } else {
      // fallback: create lightweight option and fetch investments/history using id
      handleClientSelection({
        value: option.value,
        label: option.label,
        raw: null
      });
      fetchClientInvestments(option.value);
      fetchCommunicationHistory(option.value);
    }
  };

  const validateForm = () => {
    if (!selectedClientOption) {
      CreateAlert('Please select a client first!', 'Warning', setAlert);
      return false;
    }
    if (!remark || remark.trim().length === 0) {
      CreateAlert('Please add a remark for this communication!', 'Warning', setAlert);
      return false;
    }
    return true;
  };

  const handleSaveCommunication = async () => {
    logAuditEvent(moduleName, 'Save Communication Click', selectedClientOption ? `ClientId=${selectedClientOption.value}` : 'No client selected');
    if (userModulePermission.AddModule !== 1) {
      CreateAlert("You don't have permission to add entries!", 'Error', setAlert);
      return;
    }
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ClientId: selectedClientOption.value,
        ClientCompanyTypeIds: selectedCompanyTypes.map((type) => type.value),
        Remark: remark.trim(),
        NextFollowupDate: nextFollowupDate ? moment(nextFollowupDate).format('YYYY-MM-DD HH:mm:ss') : null,
        IsCompleted: 0,
        CommunicationDate: new Date().toISOString(),
        UserId: getStorageValues('userId')
      };
      const response = await axios.post(getApiBaseUrl() + '/saveClientCommunication', payload);
      if (response.status === 200) {
        CreateAlert(response.data.message || 'Communication logged!', 'Success', setAlert);
        logAuditEvent(moduleName, 'Communication Saved', `ClientId=${selectedClientOption.value}`);
        setRemark('');
        setNextFollowupDate(null);
        fetchCommunicationHistory(selectedClientOption.value);
      } else {
        CreateAlert(response.data.message || 'Unable to save communication', 'Error', setAlert);
      }
    } catch (error) {
      console.log(error);
      CreateAlert('Something went wrong while saving entry!', 'Error', setAlert);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefresh = () => {
    logAuditEvent(moduleName, 'Refresh Click', 'Refreshed client communication data');
    fetchClients();
    fetchClientCompanyTypes();
    if (selectedClientOption) {
      fetchClientInvestments(selectedClientOption.value);
      fetchCommunicationHistory(selectedClientOption.value);
    }
  };

  const handleMarkCompleted = async (communicationId, isCompleted) => {
    try {
      // Optimistically update the UI
      setHistory(prevHistory =>
        prevHistory.map(item =>
          item.ClientCommunicationId === communicationId
            ? { ...item, IsCompleted: isCompleted ? 1 : 0 }
            : item
        )
      );

      const payload = {
        ClientCommunicationId: communicationId,
        IsCompleted: isCompleted ? 1 : 0,
        UserId: getStorageValues('userId')
      };
      const response = await axios.post(getApiBaseUrl() + '/updateClientCommunicationCompleted', payload);
      if (response.status === 200) {
        CreateAlert(response.data.message || 'Followup status updated!', 'Success', setAlert);
        // Refetch to ensure data consistency
        if (selectedClientOption) {
          fetchCommunicationHistory(selectedClientOption.value);
        }
      } else {
        CreateAlert(response.data.message || 'Unable to update status', 'Error', setAlert);
        // Revert optimistic update on error
        if (selectedClientOption) {
          fetchCommunicationHistory(selectedClientOption.value);
        }
      }
    } catch (error) {
      console.log(error);
      CreateAlert('Something went wrong while updating status!', 'Error', setAlert);
      // Revert optimistic update on error
      if (selectedClientOption) {
        fetchCommunicationHistory(selectedClientOption.value);
      }
    }
  };

  const handleEditEntry = async ({ ClientCommunicationId, ClientId, Remark, NextFollowupDate }) => {
    try {
      const payload = {
        ClientCommunicationId,
        ClientId: ClientId || selectedClientOption?.value,
        ClientCompanyTypeIds: [],
        Remark,
        NextFollowupDate: NextFollowupDate ? moment(NextFollowupDate).format('YYYY-MM-DD HH:mm:ss') : null,
        IsCompleted: 0,
        CommunicationDate: new Date().toISOString(),
        UserId: getStorageValues('userId')
      };
      const response = await axios.post(getApiBaseUrl() + '/saveClientCommunication', payload);
      if (response.status === 200) {
        CreateAlert(response.data.message || 'Communication updated!', 'Success', setAlert);
        logAuditEvent(moduleName, 'Edit Followup Click', JSON.stringify({ ClientCommunicationId }));
        if (selectedClientOption) {
          fetchCommunicationHistory(selectedClientOption.value);
        }
      } else {
        CreateAlert(response.data.message || 'Unable to update followup', 'Error', setAlert);
      }
    } catch (error) {
      console.log(error);
      CreateAlert('Something went wrong while updating followup!', 'Error', setAlert);
    }
  };

  const openDeleteModal = (entry) => {
    if (!entry || !entry.ClientCommunicationId) {
      return;
    }
    setSelectedDeleteEntry(entry);
    setDeleteModalIsOpen(true);
    logAuditEvent(moduleName, 'Delete Followup Click', JSON.stringify({ ClientCommunicationId: entry.ClientCommunicationId }));
  };

  const closeDeleteModal = (forceClose = false) => {
    if (isDeleting && !forceClose) {
      return;
    }
    setDeleteModalIsOpen(false);
    setSelectedDeleteEntry(null);
  };

  const handleDeleteEntry = async () => {
    if (!selectedDeleteEntry?.ClientCommunicationId) {
      return;
    }

    setIsDeleting(true);
    try {
      const deleteId = selectedDeleteEntry.ClientCommunicationId;
      const refreshClientId = selectedClientOption?.value || selectedDeleteEntry?.ClientId || null;
      const payload = {
        ClientCommunicationId: deleteId,
        UserId: getStorageValues('userId')
      };
      const response = await axios.post(getApiBaseUrl() + '/deleteClientCommunication', payload);
      if (response.status === 200 && response.data?.success) {
        CreateAlert(response.data.message || 'Communication deleted successfully!', 'Success', setAlert);
        logAuditEvent(moduleName, 'Delete Followup Confirm', JSON.stringify({ ClientCommunicationId: deleteId }));

        setHistory((prev) => prev.filter((item) => item.ClientCommunicationId !== deleteId));
        closeDeleteModal(true);

        if (refreshClientId) {
          await fetchCommunicationHistory(refreshClientId);
        }
      } else {
        CreateAlert(response.data?.message || 'Unable to delete followup', 'Error', setAlert);
      }
    } catch (error) {
      console.log(error);
      CreateAlert(error?.response?.data?.message || 'Something went wrong while deleting followup!', 'Error', setAlert);
    } finally {
      setIsDeleting(false);
    }
  };

  const lastCommunication = history.length > 0 ? history[0] : null;

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={Module}
        showPDF={false}
        showExcel={false}
        showPrint={false}
        showRefresh={true}
        showAddButton={false}
        onRefresh={handleRefresh}
      />






      
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row">

                <div className="col-lg-6 col-sm-6 col-12">
                  <ClientDropdown
                    modelName="ClientId"
                    required={true}
                    selectedValue={selectedClientOption ? selectedClientOption.value : null}
                    onChangeDropdown={handleClientDropdownChange}
                    isDisable={isLoadingClients}
                  />
                  <div className="mb-3">
                    <label className="form-label">Client Company Types</label>
                    <Select
                      isMulti
                      value={selectedCompanyTypes}
                      onChange={(value) => setSelectedCompanyTypes(value || [])}
                      options={companyTypeOptions}
                      placeholder="Select applicable company types"
                      closeMenuOnSelect={false}
                    />
                  </div>
                </div>

                <div className="col-lg-6 col-sm-6 col-12">
                  {clientDetails ? (
                    <div className="border rounded p-3 bg-light ht150">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h5 className="mb-1">
                            {clientDetails.ClientDisplayName || clientDetails.ClientName}
                          </h5>
                          <p className="mb-1 c-black">
                            <strong>Mobile:</strong> {clientDetails.MobileNumber || '-'}
                          </p>
                          <p className="mb-1 c-black">
                            <strong>Email:</strong> {clientDetails.EmailId || '-'}
                          </p>
                        </div>
                      {/* <div className="text-end">
                          <p className="mb-1">
                            <strong>Tax Status:</strong> {clientDetails.TaxStatus || '-'}
                          </p>
                          <p className="mb-0">
                            <strong>Rating:</strong> {clientDetails.ClientRating || '-'}
                          </p>
                        </div>  */}
                      </div> 
                    {/*   <p className="mb-0 mt-2">
                        <strong>Address:</strong> {clientDetails.AddressDisplay || 'NA'}
                      </p> */}
                      {lastCommunication ? (
                        <p className="mb-0 mt-2 text-success">
                          <strong>Last communicated:</strong>{' '}
                          {moment(lastCommunication.CommunicationDate).format('DD MMM YYYY, hh:mm A')}
                        </p>
                      ) : (
                        <p className="mb-0 mt-2 text-muted">
                          No communication history available for this client.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="border rounded p-3 text-muted h-100 d-flex align-items-center justify-content-center">
                      Select a client to view quick information.
                    </div>
                  )}
                </div>
               

                <div className="col-lg-10 col-sm-6 col-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Remark <span style={{ color: 'red' }}>*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={1}
                      placeholder="Add call notes or important action items"
                      value={remark}
                      onChange={(e) => setRemark(e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-lg-2 col-sm-6 col-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Next Followup Date
                    </label>
                    <DatePicker
                      selected={nextFollowupDate}
                      onChange={(date) => setNextFollowupDate(date)}
                      showTimeSelect
                      timeFormat="hh:mm aa"
                      timeIntervals={15}
                      dateFormat="dd/MM/yyyy h:mm aa"
                      className="form-control"
                      placeholderText="Select followup date & time"
                      minDate={new Date()}
                      isClearable
                    />
                  </div>
                </div>


                
              </div>

              <div className="row mt-3">
              </div>

              <div className="d-flex justify-content-end">
                <button
                  className="btn btn-submit"
                  onClick={handleSaveCommunication}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Communication'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {clientInvestments.length > 0 && (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Client Investment Details</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Client Company Type</th>
                        <th>Client Company</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientInvestments.map((investment) => (
                        <tr key={investment.ClientInvestmentId || investment.Id}>
                          <td>{investment.ClientCompanyTypeName}</td>
                          <td>{investment.ClientCompanyName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-12">
          <ClientCommunicationHistoryGrid
            history={history}
            isLoading={isLoadingHistory}
            hasClientSelected={!!selectedClientOption}
            onMarkCompleted={handleMarkCompleted}
            onEditEntry={handleEditEntry}
            onDeleteEntry={openDeleteModal}
          />
        </div>
      </div>

      <Modal
        show={deleteModalIsOpen}
        onHide={closeDeleteModal}
        backdrop="static"
      >
        <ModalHeader className="alert custom-alert1 alert-danger modalheader-width">
          <ModalTitle>
            <div className="custom-alert-icon">
              <Icons.AlertOctagon className="flex-shrink-0" />
            </div>
            <div>Delete Client Communication Followup</div>
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="text-left px-2 pb-0">
            <h6>Following client communication followup will be deleted and could not be recovered!!</h6>
            <ul>
              <li><b>Client :</b> {selectedDeleteEntry?.ClientName || selectedClientOption?.label || '-'}</li>
              <li><b>Remark :</b> {selectedDeleteEntry?.Remark || '-'}</li>
              <li><b>Next Followup :</b> {selectedDeleteEntry?.NextFollowupDate ? moment(selectedDeleteEntry.NextFollowupDate).format('DD MMM YYYY, hh:mm A') : 'N/A'}</li>
            </ul>
          </div>
          <div id="DeleteClientCommunicationDetails" hidden>{JSON.stringify(selectedDeleteEntry || {})}</div>
        </ModalBody>
        <ModalFooter className="modalfooter-width">
          <button
            className="btn btn-sm btn-secondary m-1"
            name="Delete"
            onClick={closeDeleteModal}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            className="btn btn-sm btn-danger m-1"
            name="Delete"
            onClick={handleDeleteEntry}
            disabled={isDeleting || !selectedDeleteEntry?.ClientCommunicationId}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </ModalFooter>
      </Modal>








    </>
  );
};

export default ClientCommunication;

